import { Routes, Route, useLocation, Navigate } from "react-router-dom";
import useCognitoHelper from "../hooks/useCognitoHelper";
import NavigationBar from "../Fragments/NavigationBar";
import ErrorPage from "../Pages/Error";
import { Suspense, lazy, useContext } from "react";
import Spinner from "../Common/Spinner/Spinner";
import { UserContext } from "../Providers/UserProvider";

const LoginPage = lazy(() => import("../Pages/Login"));
const Home = lazy(() => import("../Pages/Home"));
const PaymentPageMain = lazy(
  () => import("../Pages/Settings/PaymentSettings/PaymentPageMain")
);
const PaymentSetupStatusPage = lazy(
  () => import("../Pages/Settings/PaymentSettings/PaymentSetupStatusPage")
);

const WorkerBanksPageMain = lazy(
  () => import("../Pages/WorkerSettings/BanksSettings/BanksPageMain")
);

const PrivacyControl = lazy(
  () => import("../Pages/Settings/PrivacyControl/PrivacyControl")
);
const ResetPassword = lazy(
  () => import("../Pages/Settings/ResetPassword/ResetPassword")
);
const MyAccount = lazy(() => import("../Pages/Settings/MyAccount/MyAccount"));
const UserAuctionJobs = lazy(
  () => import("../Pages/UserAuctionJobs/UserAuctionJobs")
);

const JobDetail = lazy(() => import("../Pages/JobDetails/JobDetail"));
const JobList = lazy(() => import("../Pages/HistoryJobsList/JobList"));
const ActiveJobList = lazy(
  () => import("../Pages/ActiveJobsList/ActiveJobsList")
);

const WorkerAccountSettings = lazy(
  () => import("../Pages/WorkerSettings/WorkerAccountSettings")
);

const ContractorProfile = lazy(
  () => import("../Pages/ContractorProfile/ContractorProfile")
);
const JobAuctionPage = lazy(
  () => import("../Pages/JobAuctions/JobAuctionPage")
);

const ContractorJobView = lazy(
  () => import("../Pages/ContractorJobView/ContractorJobView")
);

const ApplicationRoutes = (): JSX.Element => {
  const { hasRole } = useContext(UserContext);

  return (
    <Routes>
      <Route
        path="/"
        element={
          <RequireAuthentication>
            <NavigationBar />
          </RequireAuthentication>
        }
      >
        <Route
          index={true}
          element={
            <Suspense fallback={<Spinner />}>
              <Home />
            </Suspense>
          }
        />

        {/** Payment Routes */}
        <Route
          path="user/settings/payment"
          element={
            <Suspense fallback={<Spinner />}>
              <PaymentPageMain />
            </Suspense>
          }
        />
        <Route
          path="user/settings/payment/setup/*"
          element={
            <Suspense fallback={<Spinner />}>
              <PaymentSetupStatusPage />
            </Suspense>
          }
        />

        {/** Privacy Route */}
        <Route
          path="user/settings/privacy-control"
          element={
            <Suspense fallback={<Spinner />}>
              <PrivacyControl />
            </Suspense>
          }
        />

        {/** Password Reset */}
        <Route
          path="user/settings/reset-password"
          element={
            <Suspense fallback={<Spinner />}>
              <ResetPassword />
            </Suspense>
          }
        />

        {/** User Profile */}
        <Route
          path="user/settings/account/profile"
          element={
            <Suspense fallback={<Spinner />}>
              <MyAccount />
            </Suspense>
          }
        />

        {/** User Open Jobs i.e. in auction */}
        <Route
          path="user/account/jobs/open"
          element={
            <Suspense fallback={<Spinner />}>
              <UserAuctionJobs />
            </Suspense>
          }
        />

        {/** User Jobs Assigned / Scheduled */}
        <Route
          path="user/account/jobs/assigned"
          element={
            <Suspense fallback={<Spinner />}>
              <ActiveJobList />
            </Suspense>
          }
        />

        {/** Full Job History Page */}
        <Route
          path="user/account/job/history"
          element={
            <Suspense fallback={<Spinner />}>
              <JobList />
            </Suspense>
          }
        />

        {/** Job Details Page */}
        <Route
          path="user/account/job/:id/details"
          element={
            <Suspense fallback={<Spinner />}>
              <JobDetail />
            </Suspense>
          }
        />

        {/** Worker/contractor Pages */}
        <Route
          path="contractor/settings/account/profile"
          element={
            <Suspense fallback={<Spinner />}>
              {hasRole("Jobber") ? <WorkerAccountSettings /> : null}
            </Suspense>
          }
        />
        {/** Worker Receiving Funds Routes */}
        <Route
          path="contractor/settings/banks"
          element={
            <Suspense fallback={<Spinner />}>
              <WorkerBanksPageMain />
            </Suspense>
          }
        />

        {/** Job Auction Page */}
        <Route
          path="contractor/job/auctions"
          element={
            <Suspense fallback={<Spinner />}>
              {hasRole("Jobber") ? <JobAuctionPage /> : null}
            </Suspense>
          }
        />

        {/** Contractor Profile */}
        <Route
          path="contractor/:id/profile"
          element={
            <Suspense fallback={<Spinner />}>
              <ContractorProfile />
            </Suspense>
          }
        />

        {/** Contractor Job View */}
        <Route
          path="contractor/account/job/:id/details"
          element={
            <Suspense fallback={<Spinner />}>
              {hasRole("Jobber") ? <ContractorJobView /> : null}
            </Suspense>
          }
        />
      </Route>

      <Route
        path="login"
        element={
          <Suspense fallback={<Spinner />}>
            <LoginPage />
          </Suspense>
        }
      />
      <Route
        path="signup"
        element={
          <Suspense fallback={<Spinner />}>
            <LoginPage />
          </Suspense>
        }
      />
      <Route
        path="forgot-password"
        element={
          <Suspense fallback={<Spinner />}>
            <LoginPage />
          </Suspense>
        }
      />

      <Route path="*" element={<ErrorPage />} />
    </Routes>
  );
};

const RequireAuthentication = ({ children }: { children: JSX.Element }) => {
  const location = useLocation();
  const { getAuthenticatedUser } = useCognitoHelper();

  return !getAuthenticatedUser() ? (
    <Navigate to="/login" state={{ from: location }} replace />
  ) : (
    children
  );
};

export default ApplicationRoutes;
